import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useNavigate, useParams } from "react-router-dom";
import EditRegisteration from "./Edit/EditRegister";
import EditBreak from "./Edit/EditBreak";
import EditClass from "./Edit/EditClass";
import EditPackage from "./Edit/EditPackage";
import api from "../../config/URL";
import { toast } from "react-toastify";
import fetchAllCentreManager from "../List/CentreMangerList";
import Delete from "../../components/common/Delete";
import CustomDatePicker from "../CustomDatePicker";
import PhoneInput from "react-phone-input-2";
import CopyCenterPackages from "./Edit/CopyCenterPakages";

const validationSchema = Yup.object().shape({
  centerName: Yup.string().required("*Centre Name is required"),
  code: Yup.string().required("*Code is required"),
  address: Yup.string().required("*Address is required"),
  zipCode: Yup.string()
    .matches(/^[0-9]+$/, "*Postal Code must contain only numbers")
    .min(4, "*Postal Code must be at least 4 digits")
    .max(6, "*Postal Code can't be longer than 6 digits")
    .required("*Postal Code is required"),
  mobile: Yup.string()
    .matches(
      /^(?:\+?65)?\s?(?:\d{4}\s?\d{4}|\d{3}\s?\d{3}\s?\d{4})$/,
      "*Invalid Phone Number"
    )
    .required("*Mobile Number is required"),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "*Enter a valid email address"
    )
    .required("*Email is required"),
  openingDate: Yup.date().required("*Opening Date is required"),
  uenNumber: Yup.string().required("*UEN number is required"),
  bankName: Yup.string().required("*Bank Name is required"),
  bankBranch: Yup.string().required("*Bank Branch is required"),
  bankAccountNumber: Yup.string().required("*Bank Account Number is required"),
  bankAccountName: Yup.string().required("*Bank Account Name is required"),
  invoiceNotes: Yup.string()
    .notRequired()
    .max(2000, "*The maximum length is 2000 characters"),
  target: Yup.number()
    .typeError("*Must be a number")
    .required("*Target is required")
    .positive("*Must be a positive number")
    .integer("*Must be a whole number"),
});

function CenterEdit({ handleCenterChanged }) {
  const { id } = useParams();
  const [data, setData] = useState([]);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const [teacherData, setTeacherData] = useState(null);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const navigate = useNavigate();
  const [taxTypeData, setTaxTypeData] = useState(null);
  const userName = localStorage.getItem("userName");
  const [taxData, setTaxData] = useState([]);

  useEffect(() => {
    fetchTeacher();
  }, []);

  const fetchTeacher = async () => {
    try {
      const manager = await fetchAllCentreManager();
      setTeacherData(manager);
    } catch (error) {
      toast.error(error);
    }
  };

  const formik = useFormik({
    initialValues: {
      centerName: "",
      code: "",
      userId: "",
      address: "",
      zipCode: "",
      mobile: "",
      email: "",
      openingDate: "",
      uenNumber: "",
      gst: "",
      taxRegistrationNumber: "",
      bankName: "",
      bankBranch: "",
      bankAccountNumber: "",
      bankAccountName: "",
      invoiceNotes: "",
      materialsFee: "",
      materialsTaxId: "",
      target: "",
      file: null,
      updatedBy: userName,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      const formData = new FormData();
      formData.append("centerName", values.centerName);
      formData.append("code", values.code);
      if (values.userId) {
        formData.append("userId", values.userId);
      }
      formData.append("address", values.address);
      formData.append("zipCode", values.zipCode);
      formData.append("mobile", values.mobile);
      formData.append("email", values.email);
      formData.append("openingDate", values.openingDate);
      formData.append("uenNumber", values.uenNumber);
      formData.append("gst", values.gst);
      formData.append("taxRegistrationNumber", values.taxRegistrationNumber);
      formData.append("bankName", values.bankName);
      formData.append("bankBranch", values.bankBranch);
      formData.append("bankAccountNumber", values.bankAccountNumber);
      formData.append("bankAccountName", values.bankAccountName);
      formData.append("invoiceNotes", values.invoiceNotes);
      formData.append("file", values.file);
      formData.append("updatedBy", userName);
      formData.append("materialsFee", values.materialsFee);
      formData.append("materialsTaxId", values.materialsTaxId || "");
      formData.append("target", values.target);
      try {
        const response = await api.put(`/updateCenters/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        console.log("FormData is Data;", response);
        if (response.status === 200) {
          toast.success(response.data.message);
          navigate("/center");
          handleCenterChanged();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        if (error.response.status === 409) {
          toast.warning(error?.response?.data?.message);
        } else {
          toast.error(error.response.data.message);
        }
      } finally {
        setLoadIndicator(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const scrollToError = (errors) => {
    const errorField = Object.keys(errors)[0];
    const errorElement = document.querySelector(`[name="${errorField}"]`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      errorElement.focus();
    }
  };

  const fetchTaxData = async () => {
    try {
      const response = await api.get("getAllTaxSetting");
      setTaxData(response.data);
    } catch (error) {
      toast.error("Error fetching tax data:", error);
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      scrollToError(formik.errors);
    }
  }, [formik.submitCount, formik.errors]);

  useEffect(() => {
    const getData = async () => {
      const response = await api.get(`/getAllCenterById/${id}`);
      const formattedData = {
        ...response.data,
        openingDate: response.data.openingDate
          ? new Date(response.data.openingDate).toISOString().substring(0, 10)
          : null,
      };
      formik.setValues(formattedData);
      setData(response.data);
    };

    getData();
    fetchTeacher();
    fetchTaxData();
  }, [id]);

  const refreshData = async () => {
    try {
      const response = await api.get(`/getAllCenterById/${id}`);
      setData(response.data);
    } catch (error) {
      toast.error("Error Fetching Data");
    }
  };

  const handleCopySuccess = () => {
    setSelectedPackages([]); // Reset selected packages after successful copy
    refreshData(); // Refresh data to reflect changes
  };

  const handlePackageSelection = (packageId) => {
    setSelectedPackages((prev) =>
      prev.includes(packageId)
        ? prev.filter((id) => id !== packageId)
        : [...prev, packageId]
    );
  };

  return (
    <div className="container-fluid">
      <ol
        className="breadcrumb my-3 px-2"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Centre Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/center" className="custom-breadcrumb">
            Centre Listing
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Centre Listing Edit
        </li>
      </ol>
      <form onSubmit={formik.handleSubmit}>
        <div className="card">
          <div
            className="d-flex px-4 justify-content-between align-items-center p-1 mb-4"
            style={{ background: "#f5f7f9" }}
          >
            <div className="d-flex align-items-center">
              <div className="d-flex">
                <div className="dot active"></div>
              </div>
              <span className="me-2 text-muted">Edit Centre</span>
            </div>
            <div className="my-2 pe-3 d-flex align-items-center">
              <Link to="/center">
                <button type="button" className="btn btn-sm btn-border">
                  Back
                </button>
              </Link>
                
              <button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                Update
              </button>
            </div>
          </div>
          <div className="container-fluid px-4">
            <div className="row">
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Centre Name<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="centerName"
                    className={`form-control ${
                      formik.touched.centerName && formik.errors.centerName
                        ? "is-invalid"
                        : ""
                    }`}
                    aria-label="Username"
                    aria-describedby="basic-addon1"
                    {...formik.getFieldProps("centerName")}
                  />
                  {formik.touched.centerName && formik.errors.centerName && (
                    <div className="invalid-feedback">
                      {formik.errors.centerName}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Code<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("code")}
                    type="text"
                    className={`form-control ${
                      formik.touched.code && formik.errors.code
                        ? "is-invalid"
                        : ""
                    }`}
                    placeholder=""
                  />
                  {formik.touched.code && formik.errors.code && (
                    <div className="invalid-feedback">{formik.errors.code}</div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-3">
                <label className="form-label">Centre Manager</label>
                <select
                  {...formik.getFieldProps("userId")}
                  name="userId"
                  className={`form-select ${
                    formik.touched.userId && formik.errors.userId
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <option selected></option>
                  {teacherData &&
                    teacherData.map((manager) => (
                      <option key={manager.id} value={manager.id}>
                        {manager.userNames}
                      </option>
                    ))}
                </select>
                {formik.touched.userId && formik.errors.userId && (
                  <div className="invalid-feedback">{formik.errors.userId}</div>
                )}
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Address<span className="text-danger">*</span>
                  </label>
                  <textarea
                    className={`form-control ${
                      formik.touched.address && formik.errors.address
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("address")}
                    id="exampleFormControlTextarea1"
                    rows="3"
                    onBlur={formik.handleBlur}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        console.log(
                          "Enter key pressed: moving to the next line"
                        );
                      }
                    }}
                  ></textarea>
                  {formik.touched.address && formik.errors.address && (
                    <div className="invalid-feedback">
                      {formik.errors.address}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Postal Code<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("zipCode")}
                    type="text"
                    className={`form-control ${
                      formik.touched.zipCode && formik.errors.zipCode
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.zipCode && formik.errors.zipCode && (
                    <div className="invalid-feedback">
                      {formik.errors.zipCode}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <label className="form-label">
                  Mobile Number<span className="text-danger">*</span>
                </label>
                <PhoneInput
                  country={"sg"}
                  enableSearch={true}
                  inputProps={{
                    name: "mobile",
                    required: true,
                    onBlur: formik.handleBlur,
                  }}
                  value={formik.values.mobile}
                  onChange={(phone) =>
                    formik.setFieldValue("mobile", "+" + phone)
                  }
                  containerClass="phone-input-container"
                  inputClass="phone-input"
                  buttonClass="phone-flag-button"
                />
                {formik.touched.mobile && formik.errors.mobile && (
                  <div className="text-danger">
                    <small>{formik.errors.mobile}</small>
                  </div>
                )}
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Email<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("email")}
                    type="text"
                    className={`form-control ${
                      formik.touched.email && formik.errors.email
                        ? "is-invalid"
                        : ""
                    }`}
                    aria-label="Username"
                    aria-describedby="basic-addon1"
                  />
                  {formik.touched.email && formik.errors.email && (
                    <div className="invalid-feedback">
                      {formik.errors.email}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Opening Date<span className="text-danger">*</span>
                  </label>
                  <CustomDatePicker
                    name="openingDate"
                    value={formik.values.openingDate}
                    onChange={(date) => {
                      const formattedDate = date
                        ? new Date(date).toISOString().split("T")[0]
                        : null;
                      formik.setFieldValue("openingDate", formattedDate);
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.errors.openingDate}
                    touched={formik.touched.openingDate}
                    showPopperArrow={true}
                  />
                  {formik.touched.openingDate && formik.errors.openingDate && (
                    <div className="error text-danger">
                      {formik.errors.openingDate}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <div>
                    <label
                      for="exampleFormControlInput1"
                      className="form-label"
                    >
                      GST<span className="text-danger">*</span>
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="gst"
                      id="inlineRadio1"
                      value="true"
                      onChange={() => formik.setFieldValue("gst", true)}
                      checked={formik.values.gst === true}
                    />
                    <label className="form-check-label" htmlFor="inlineRadio1">
                      Yes
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="gst"
                      id="inlineRadio2"
                      value="false"
                      onChange={() => formik.setFieldValue("gst", false)}
                      checked={formik.values.gst === false}
                    />
                    <label className="form-check-label" htmlFor="inlineRadio2">
                      No
                    </label>
                  </div>
                  {formik.errors.gst && formik.touched.gst && (
                    <div className="text-danger" style={{ fontSize: ".875em" }}>
                      {formik.errors.gst}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    UEN Number<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("uenNumber")}
                    type="text"
                    className={`form-control ${
                      formik.touched.uenNumber && formik.errors.uenNumber
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.uenNumber && formik.errors.uenNumber && (
                    <div className="invalid-feedback">
                      {formik.errors.uenNumber}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    TAX Registration Number
                  </label>
                  <input
                    {...formik.getFieldProps("taxRegistrationNumber")}
                    type="text"
                    className={`form-control ${
                      formik.touched.taxRegistrationNumber &&
                      formik.errors.taxRegistrationNumber
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.taxRegistrationNumber &&
                    formik.errors.taxRegistrationNumber && (
                      <div className="invalid-feedback">
                        {formik.errors.taxRegistrationNumber}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Bank Name<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("bankName")}
                    type="text"
                    className={`form-control ${
                      formik.touched.bankName && formik.errors.bankName
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.bankName && formik.errors.bankName && (
                    <div className="invalid-feedback">
                      {formik.errors.bankName}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Bank Branch<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("bankBranch")}
                    type="text"
                    className={`form-control ${
                      formik.touched.bankBranch && formik.errors.bankBranch
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.bankBranch && formik.errors.bankBranch && (
                    <div className="invalid-feedback">
                      {formik.errors.bankBranch}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Bank Account Name<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("bankAccountName")}
                    type="text"
                    className={`form-control ${
                      formik.touched.bankAccountName &&
                      formik.errors.bankAccountName
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.bankAccountName &&
                    formik.errors.bankAccountName && (
                      <div className="invalid-feedback">
                        {formik.errors.bankAccountName}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Bank Account Number<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("bankAccountNumber")}
                    type="text"
                    className={`form-control ${
                      formik.touched.bankAccountNumber &&
                      formik.errors.bankAccountNumber
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.bankAccountNumber &&
                    formik.errors.bankAccountNumber && (
                      <div className="invalid-feedback">
                        {formik.errors.bankAccountNumber}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="text-start mt-2">
                  <label htmlFor="" className="mb-1 fw-medium">
                    <small>QR Code</small>
                    <span className="text-danger">*</span>
                  </label>
                  <br />
                  <input
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    name="file"
                    className="form-control"
                    onChange={(event) => {
                      formik.setFieldValue("file", event.target.files[0]);
                    }}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.file && formik.errors.file && (
                    <div className="error text-danger">
                      <small>{formik.errors.file}</small>
                    </div>
                  )}
                </div>
                <img
                  src={data.qrCode}
                  className="img-fluid ms-2 w-50 rounded mt-2"
                  alt="Profile"
                />
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Materials Fee(inclusive of tax)
                  </label>
                  <input
                    {...formik.getFieldProps("materialsFee")}
                    type="text"
                    className={`form-control   ${
                      formik.touched.materialsFee && formik.errors.materialsFee
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.materialsFee &&
                    formik.errors.materialsFee && (
                      <div className="invalid-feedback">
                        {formik.errors.materialsFee}
                      </div>
                    )}
                </div>
              </div>
              <div className="col-md-6 col-12 mb-2">
                <label for="exampleFormControlInput1" className="form-label">
                  Materials Tax
                </label>
                <select
                  {...formik.getFieldProps("materialsTaxId")}
                  type="text"
                  className={`form-select ${
                    formik.touched.materialsTaxId &&
                    formik.errors.materialsTaxId
                      ? "is-invalid"
                      : ""
                  }`}
                >
                  <option value=""></option>
                  {taxData &&
                    taxData.map((tax) => (
                      <option key={tax.id} value={tax.id}>
                        {tax.taxType}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-3">
                  <label for="exampleFormControlInput1" className="form-label">
                    Target<span className="text-danger">*</span>
                  </label>
                  <input
                    {...formik.getFieldProps("target")}
                    type="text"
                    className={`form-control ${
                      formik.touched.target && formik.errors.target
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.target && formik.errors.target && (
                    <div className="invalid-feedback">
                      {formik.errors.target}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-12">
                <label htmlFor="invoiceNotes" className="form-label">
                  Invoice Notes
                </label>
                <div className="input-group mb-3">
                  <textarea
                    name="invoiceNotes"
                    className="form-control"
                    {...formik.getFieldProps("invoiceNotes")}
                    id="invoiceNotes"
                    rows="5"
                  ></textarea>
                </div>
                {formik.touched.invoiceNotes && formik.errors.invoiceNotes && (
                  <div className="text-danger">
                    {formik.errors.invoiceNotes}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="container-fluid">
            <div className="row">
              {/* Centre Registrations */}
              <div className="col-md-12 col-12 mt-4">
                <h5 className="headColor mb-3">Centre Registrations</h5>
                <table className="table table-border-solid">
                  <thead>
                    <tr>
                      <th scope="col" className="fw-medium">
                        S.No
                      </th>
                      <th scope="col" className="fw-medium">
                        Effective Date
                      </th>
                      <th scope="col" className="fw-medium">
                        Amount Including (GST)
                      </th>
                      <th scope="col" className="fw-medium">
                        Tax Type
                      </th>
                      <th scope="col" className="fw-medium text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.centerRegistrations &&
                      data.centerRegistrations.map((registration, index) => (
                        <tr key={index} className="mt-1">
                          <td>{index + 1}</td>
                          <td>
                            {registration.effectiveDate?.substring(0, 10)}
                          </td>
                          <td>{registration.amount}</td>
                          <td>
                            {taxTypeData &&
                              taxTypeData.map((tax) =>
                                parseInt(registration.taxId) === tax.id
                                  ? tax.taxType || "--"
                                  : ""
                              )}
                          </td>
                          <td className="text-center">
                            <EditRegisteration
                              id={registration.id}
                              onSuccess={refreshData}
                            />
                            <Delete
                              onSuccess={refreshData}
                              path={`/deleteCenterRegistrations/${registration.id}`}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {/* Centre Break */}
              <div className="col-md-12 col-12 mt-4">
                <h5 className="headColor mb-3">Centre Break</h5>
                <table className="table table-border-solid">
                  <thead>
                    <tr>
                      <th scope="col" className="fw-medium">
                        S.No
                      </th>
                      <th scope="col" className="fw-medium">
                        Break Name
                      </th>
                      <th scope="col" className="fw-medium">
                        From Date
                      </th>
                      <th scope="col" className="fw-medium">
                        To date
                      </th>
                      <th scope="col" className="fw-medium text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.centerBreaks &&
                      data.centerBreaks.map((centerBreak, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{centerBreak.breakName}</td>
                          <td>{centerBreak.fromDate.substring(0, 10)}</td>
                          <td>{centerBreak.toDate.substring(0, 10)}</td>
                          <td className="text-center">
                            <EditBreak
                              id={centerBreak.id}
                              onSuccess={refreshData}
                            />
                            <Delete
                              onSuccess={refreshData}
                              path={`/deleteCenterBreaks/${centerBreak.id}`}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {/* class Room  */}
              <div className="col-md-12 col-12 mt-4">
                <h5 className="headColor mb-3">Centre Classroom</h5>
                <div className="table-responsive">
                  <table className="table table-border-solid">
                    <thead>
                      <tr>
                        <th scope="col" className="fw-medium">
                          S.No
                        </th>
                        <th
                          scope="col"
                          className="fw-medium"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Classroom Name
                        </th>
                        <th
                          scope="col"
                          className="fw-medium"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Classroom Code
                        </th>
                        <th
                          scope="col"
                          className="fw-medium"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Classroom Type
                        </th>
                        <th
                          scope="col"
                          className="fw-medium"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Capacity
                        </th>
                        <th
                          scope="col"
                          className="fw-medium"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          Description
                        </th>
                        <th scope="col" className="fw-medium ps-4">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.centerClassRooms &&
                        data.centerClassRooms.map((centerClassRoom, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{centerClassRoom.classRoomName}</td>
                            <td>{centerClassRoom.classRoomCode}</td>
                            <td>{centerClassRoom.classRoomType}</td>
                            <td>{centerClassRoom.capacity}</td>
                            <td className="text-break">
                              {centerClassRoom.description}
                            </td>
                            <td>
                              <EditClass
                                id={centerClassRoom.id}
                                onSuccess={refreshData}
                              />
                              <Delete
                                onSuccess={refreshData}
                                path={`/deleteCenterClassRooms/${centerClassRoom.id}`}
                              />
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Package  */}
              <div className="col-md-12 col-12 mt-4">
                <div className="d-flex justify-content-between align-items-center">
                  <h5 className="headColor mb-3">Centre Package</h5>
                  <CopyCenterPackages
                    packageIds={selectedPackages}
                    onSuccess={handleCopySuccess}
                  />
                </div>
                <table className="table table-border-solid">
                  <thead>
                    <tr>
                      <th scope="col" className="fw-medium">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={
                            data.centerPackages &&
                            data.centerPackages.length > 0 &&
                            selectedPackages.length ===
                              data.centerPackages.length
                          }
                          onChange={() =>
                            setSelectedPackages(
                              selectedPackages.length ===
                                data.centerPackages.length
                                ? []
                                : data.centerPackages.map((pkg) => pkg.id)
                            )
                          }
                        />
                      </th>
                      <th scope="col" className="fw-medium">
                        S.No
                      </th>
                      <th scope="col" className="fw-medium">
                        Package
                      </th>
                      <th scope="col" className="fw-medium">
                        Number Of Lesson
                      </th>
                      <th scope="col" className="fw-medium text-center">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.centerPackages &&
                      data.centerPackages.map((centerPackage, index) => (
                        <tr key={index}>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={selectedPackages.includes(
                                centerPackage.id
                              )}
                              onChange={() =>
                                handlePackageSelection(centerPackage.id)
                              }
                            />
                          </td>
                          <td>{index + 1}</td>
                          <td>{centerPackage.packageName || "--"}</td>
                          <td>{centerPackage.noOfLesson || "--"}</td>
                          <td className="text-center">
                            <EditPackage
                              id={centerPackage.id}
                              onSuccess={refreshData}
                            />
                            <Delete
                              onSuccess={refreshData}
                              path={`/deleteCenterPackages/${centerPackage.id}`}
                            />
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CenterEdit;
